// Board.jsx
// Renders the 15x15 Ludo board exactly matching the reference image:
// colored home corners with 4-dot panels, colored arm stripes with
// directional arrows, white track cells with thin grid lines, star
// safe-cells, and the 4-triangle center "home" pinwheel.
import React from 'react';
import {
  COLOR_HEX,
  GRID_SIZE,
  STAR_CELLS,
  HOME_STRETCH,
  BASE_SLOTS,
  getCellForStep,
  STEPS_TO_FINISH,
  START_INDEX,
  MAIN_TRACK,
} from './ludoBoard.js';
import Token from './Token.jsx';

const CELL = 100 / GRID_SIZE; // percentage units, board is square viewBox 0..100

function cellStyle(row, col, extra = {}) {
  return {
    position: 'absolute',
    left: `${col * CELL}%`,
    top: `${row * CELL}%`,
    width: `${CELL}%`,
    height: `${CELL}%`,
    ...extra,
  };
}

function StarMark({ row, col }) {
  return (
    <div style={cellStyle(row, col)} className="ludo-star-cell">
      <svg viewBox="0 0 24 24" className="ludo-star-svg">
        <polygon
          points="12,2 14.7,8.8 22,9.3 16.3,14 18.2,21.2 12,17.1 5.8,21.2 7.7,14 2,9.3 9.3,8.8"
          fill="#ffffff"
          stroke="#999"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}

function ArrowMark({ row, col, direction, color }) {
  // direction: 'down' | 'up' | 'left' | 'right'
  const rotation = { down: 0, left: 90, up: 180, right: 270 }[direction];
  return (
    <div style={cellStyle(row, col)} className="ludo-arrow-cell">
      <svg
        viewBox="0 0 24 24"
        style={{ transform: `rotate(${rotation}deg)` }}
        className="ludo-arrow-svg"
      >
        <path
          d="M12 2 L12 18 M12 18 L6 12 M12 18 L18 12"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function HomeBase({ color, top, left }) {
  return (
    <div
      className="ludo-base"
      style={{
        position: 'absolute',
        top: `${top * CELL}%`,
        left: `${left * CELL}%`,
        width: `${CELL * 6}%`,
        height: `${CELL * 6}%`,
        background: COLOR_HEX[color],
      }}
    >
      <div className="ludo-base-inner">
        <div className="ludo-base-dot" />
        <div className="ludo-base-dot" />
        <div className="ludo-base-dot" />
        <div className="ludo-base-dot" />
      </div>
    </div>
  );
}

function CenterHome() {
  // Pinwheel of 4 triangles meeting in the middle, cols/rows 6-8 (3x3 block)
  return (
    <div
      style={{
        position: 'absolute',
        top: `${6 * CELL}%`,
        left: `${6 * CELL}%`,
        width: `${CELL * 3}%`,
        height: `${CELL * 3}%`,
      }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="none">
        <polygon points="0,0 50,50 100,0" fill={COLOR_HEX.red} />
        <polygon points="100,0 50,50 100,100" fill={COLOR_HEX.green} />
        <polygon points="100,100 50,50 0,100" fill={COLOR_HEX.yellow} />
        <polygon points="0,100 50,50 0,0" fill={COLOR_HEX.blue} />
      </svg>
    </div>
  );
}

export default function Board({ tokens, movableTokenIds, onTokenClick, lastEvent, animatingTokenId = null }) {
  const getDisplayStep = (token) => token.visualStep ?? token.step;

  const isOnFinishTile = (token) => {
    if (token.visualStep != null) return token.visualStep >= STEPS_TO_FINISH;
    return token.finished;
  };

  const isInBase = (token) => getDisplayStep(token) === 0 && !isOnFinishTile(token);

  const isOnTrack = (token) => {
    const step = getDisplayStep(token);
    if (step <= 0) return false;
    if (token.visualStep != null) return token.visualStep < STEPS_TO_FINISH;
    return !token.finished;
  };

  // Build a map of cell -> tokens occupying it, so we can stack/offset
  // multiple tokens sharing a cell.
  const cellOccupants = {};
  for (const color of Object.keys(tokens)) {
    for (const token of tokens[color]) {
      if (!isOnTrack(token)) continue;
      const cell = getCellForStep(color, getDisplayStep(token));
      if (!cell) continue;
      const key = `${cell[0]}_${cell[1]}`;
      if (!cellOccupants[key]) cellOccupants[key] = [];
      cellOccupants[key].push(token);
    }
  }

  return (
    <div className="ludo-board">
      {/* base grid lines for the whole 15x15, only visible on track cells via CSS */}
      <div className="ludo-grid-lines">
        {Array.from({ length: GRID_SIZE }).map((_, r) =>
          Array.from({ length: GRID_SIZE }).map((_, c) => {
            const isHome =
              (r < 6 && c < 6) || (r < 6 && c > 8) || (r > 8 && c < 6) || (r > 8 && c > 8);
            const isCenter = r >= 6 && r <= 8 && c >= 6 && c <= 8;
            if (isHome || isCenter) return null;

            // Check if this cell is a colored starting cell for any color
            const startColor = Object.entries(START_INDEX).find(
              ([, idx]) => MAIN_TRACK[idx][0] === r && MAIN_TRACK[idx][1] === c
            )?.[0];

            return (
              <div
                key={`${r}-${c}`}
                style={cellStyle(r, c, startColor ? { background: COLOR_HEX[startColor] } : {})}
                className={`ludo-cell${startColor ? ' ludo-stretch-cell' : ''}`}
              />
            );
          })
        )}
      </div>

      {/* colored arm stripes (home stretches) */}
      {Object.entries(HOME_STRETCH).map(([color, cells]) =>
        cells.map(([r, c], i) => (
          <div
            key={`${color}-stretch-${i}`}
            style={cellStyle(r, c, { background: COLOR_HEX[color] })}
            className="ludo-cell ludo-stretch-cell"
          />
        ))
      )}

      {/* 4 home base corners */}
      <HomeBase color="blue" top={0} left={0} />
      <HomeBase color="red" top={0} left={9} />
      <HomeBase color="yellow" top={9} left={0} />
      <HomeBase color="green" top={9} left={9} />

      {/* center pinwheel */}
      <CenterHome />

      {/* arrows: direction tokens travel when entering the colored run */}
      <ArrowMark row={0} col={7} direction="down" color={COLOR_HEX.red} />
      <ArrowMark row={7} col={14} direction="left" color={COLOR_HEX.green} />
      <ArrowMark row={14} col={7} direction="up" color={COLOR_HEX.yellow} />
      <ArrowMark row={7} col={0} direction="right" color={COLOR_HEX.blue} />

      {/* star safe cells */}
      {STAR_CELLS.map(([r, c], i) => (
        <StarMark key={`star-${i}`} row={r} col={c} />
      ))}

      {/* base-resting tokens (still in yard) */}
      {Object.entries(tokens).map(([color, colorTokens]) =>
        colorTokens.map((token, i) => {
          if (!isInBase(token)) return null;
          const [row, col] = BASE_SLOTS[color][i];
          const isMovable = movableTokenIds.includes(token.id);
          return (
            <Token
              key={token.id}
              color={color}
              row={row}
              col={col}
              cellSize={CELL}
              isMovable={isMovable}
              onClick={() => onTokenClick(token.id)}
              justLanded={lastEvent && lastEvent.color === color}
              isAnimating={animatingTokenId === token.id}
            />
          );
        })
      )}

      {/* on-track / home-stretch tokens, offset within shared cells */}
      {Object.entries(cellOccupants).map(([key, occupants]) => {
        const [row, col] = key.split('_').map(Number);
        return occupants.map((token, i) => {
          const isMovable = movableTokenIds.includes(token.id);
          const offset = stackOffset(i, occupants.length);
          return (
            <Token
              key={token.id}
              color={token.color}
              row={row + offset.dy}
              col={col + offset.dx}
              cellSize={CELL}
              small={occupants.length > 1}
              isMovable={isMovable}
              onClick={() => onTokenClick(token.id)}
              isAnimating={animatingTokenId === token.id}
            />
          );
        });
      })}

      {/* finished tokens parked at the finish tile */}
      {Object.entries(tokens).map(([color, colorTokens]) => {
        const finished = colorTokens.filter((t) => isOnFinishTile(t));
        if (finished.length === 0) return null;
        return finished.map((token, i) => {
          const cell = getCellForStep(color, STEPS_TO_FINISH);
          const offset = stackOffset(i, finished.length);
          return (
            <Token
              key={token.id}
              color={color}
              row={cell[0] + offset.dy}
              col={cell[1] + offset.dx}
              cellSize={CELL}
              small={finished.length > 1}
              isMovable={false}
              onClick={() => {}}
              isAnimating={animatingTokenId === token.id}
            />
          );
        });
      })}
    </div>
  );
}

function stackOffset(index, total) {
  if (total <= 1) return { dx: 0, dy: 0 };
  const positions = [
    { dx: -0.2, dy: -0.2 },
    { dx: 0.2, dy: -0.2 },
    { dx: -0.2, dy: 0.2 },
    { dx: 0.2, dy: 0.2 },
  ];
  return positions[index % positions.length];
}
